import {createSelector} from 'reselect';
import {errors, success} from '../utils/errorEvents';
import {getTranslations, getTripData, getListingPage} from './store';

// Peels off just arrive/depart date text
const getTripDataDates = createSelector(
    [getTripData],
    (tripData) => {
        const {arriveDateText, departDateText} = tripData;

        return {
            arriveDateText,
            departDateText
        };
    }
);

// Determines if both date fields have text
const getIncompleteDates = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.validDates.datesEmpty;
    }
);

// Returns true if dates are not empty, dates meet listing criteria, and no date-specific quote error exists
const getValidDates = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.validDates.datesValid;
    }
);

// Return whether to show error state on date input
const getValidOrEmptyDates = createSelector(
    [getValidDates, getIncompleteDates],
    (validDates, incompleteDates) => {
        return validDates || incompleteDates;
    }
);

// Returns date success obj with edap info and translation info
const getDateSuccess = createSelector(
    [getTripData, getValidDates, getTranslations],
    (tripData, validDates, translations) => {
        let successMsg;

        if (validDates) {
            successMsg = success.DATES_SUCCESS;
            successMsg.label = translations[successMsg.translationKey];
        }
        return successMsg || null;
    }
);

// Returns true if dates don't meet minstay requirement on page load
const hasInitialMinStayError = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.validDates.hasMinStayError;
    }
);

const hasInitialMinPriorNotifyError = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.validDates.hasMinPriorNotifyError;
    }
);

const hasInitialSameDayArrivalError = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.validDates.hasSameDayArrivalError;
    }
);

const hasAvailabilityError = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.validDates.hasUnavailableDates;
    }
);

// Returns date error obj with edap info and translation info for unavailability (currently)
const getDatesError = createSelector(
    [
        getTripData,
        getValidDates,
        hasInitialMinStayError,
        hasInitialMinPriorNotifyError,
        hasInitialSameDayArrivalError,
        hasAvailabilityError,
        getTranslations
    ],
    /* eslint-disable complexity */
    (
        tripData,
        validDates,
        minStayError,
        minPriorNotifyError,
        sameDayArrivalError,
        availabilityError,
        translations
    ) => {
        let error;

        if (!validDates) {
            // TODO: needs stale calendar warning
            if (sameDayArrivalError) {
                error = errors.SAME_DAY_ARRIVAL;
            } else if (minPriorNotifyError) {
                error = errors.MIN_PRIOR_NOTIFY;
            } else if (minStayError && !availabilityError) {
                error = errors.MIN_STAY_NOT_MET;
            } else {
                error = errors.UNAVAILABLE_DATES;
            }
            error.label = translations[error.translationKey];
        }

        return error || null;
    }
);
/* eslint-enable complexity */

export {
    getTripDataDates,
    getIncompleteDates,
    getValidDates,
    getValidOrEmptyDates,
    getDateSuccess,
    hasInitialMinStayError,
    hasInitialMinPriorNotifyError,
    hasInitialSameDayArrivalError,
    hasAvailabilityError,
    getDatesError
};
